'use client';

import { useState } from 'react';

import Image from 'next/image';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import {
  BubbleAutopilot,
  BubbleInbound,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import { TemplatesList } from '@/components/sections/templates-list';
import Testimonials from '@/components/sections/testimonials';
import { MagicalText } from '@/components/ui/magical-text';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

type ComparisonStatus = 'best' | 'worst' | 'neutral';

interface ComparisonRow {
  metric: string;
  agent5: { value: string; status: ComparisonStatus };
  twilio: { value: string; status: ComparisonStatus };
}

const CONTENT = {
  en: {
    eyebrow: 'Helpdesks',
    heroTitleA: 'The AI agent',
    heroTitleB: 'doesn’t have',
    heroSubtitleA: 'Production-ready voice + chat AI for Flex.',
    heroSubtitleB:
      'No developers, no custom code, live in minutes — not months.',
    inboundTitle: 'Inbound call · +1 (415) 555-0148',
    inboundPreview: 'Hi, I need to reschedule my delivery for tomorrow.',
    autopilotTitle: 'Autopilot · call #5188',
    autopilotSteps: [
      'Picked up call in Flex',
      'Identified caller from CRM',
      'Pulled order #4521',
      'Rescheduled · Fri May 17',
      'Wrap-up logged · CSAT 5★',
    ],
    vs: 'vs',
  },
  ar: {
    eyebrow: 'أنظمة الدعم',
    heroTitleA: 'الوكيل الذكي اللي',
    heroTitleB: 'ما عنده',
    heroSubtitleA: 'AI للصوت والمحادثة، جاهز للإنتاج داخل Flex.',
    heroSubtitleB: 'بدون مطوّرين، وبدون كود مخصّص. جاهز خلال دقائق — مو شهور.',
    inboundTitle: 'مكالمة واردة · +1 (415) 555-0148',
    inboundPreview: 'هلا، أبي أعيد جدولة توصيل طلبي لبكرة.',
    autopilotTitle: 'Autopilot · مكالمة #5188',
    autopilotSteps: [
      'ردّ على المكالمة في Flex',
      'تعرّف على المتصل من الـ CRM',
      'سحب الطلب #4521',
      'أعاد الجدولة · الجمعة ١٧ مايو',
      'سجّل الملخّص · CSAT 5★',
    ],
    vs: 'مقابل',
  },
} as const;

export default function TwilioFlexPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData: ComparisonRow[] = [
    {
      metric: t.helpdesk['Built-in AI Agent'],
      agent5: { value: t.helpdesk['Yes - Full featured'], status: 'best' },
      twilio: { value: t.helpdesk['No native AI'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Setup Complexity'],
      agent5: { value: t.helpdesk['5 minutes'], status: 'best' },
      twilio: { value: t.helpdesk['Build from scratch'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Capabilities'],
      agent5: { value: t.helpdesk['Advanced multi-model'], status: 'best' },
      twilio: { value: t.helpdesk['Requires custom build'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Developer Required'],
      agent5: { value: t.helpdesk['No'], status: 'best' },
      twilio: { value: t.helpdesk['Yes'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Time to Deploy'],
      agent5: { value: t.helpdesk['Same day'], status: 'best' },
      twilio: { value: t.helpdesk['Weeks/months'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Maintenance'],
      agent5: { value: t.helpdesk['Fully managed'], status: 'best' },
      twilio: { value: t.helpdesk['Self-maintained'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Context Understanding'],
      agent5: { value: t.helpdesk['Deep context'], status: 'best' },
      twilio: { value: t.helpdesk['DIY implementation'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Ready to Use'],
      agent5: { value: t.helpdesk['Out of the box'], status: 'best' },
      twilio: { value: t.helpdesk['Custom development'], status: 'worst' },
    },
  ];

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={
          <>
            {c.heroTitleA}{' '}
            <span className="whitespace-nowrap">
              <Image
                src="/helpdicks/flex.png"
                alt="Twilio Flex"
                width={48}
                height={48}
                className="mr-1 inline-block h-[0.8em] w-[0.8em] rounded-[0.18em] align-[-0.1em]"
              />
              <span className="text-[#F22F46]">Twilio Flex</span>
            </span>{' '}
            {c.heroTitleB}
          </>
        }
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        videoSrc="/home/features/ocn.mp4"
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleInbound
                source="Twilio Flex"
                sourceLogo="/helpdicks/flex.png"
                title={c.inboundTitle}
                preview={c.inboundPreview}
              />
            ),
          },
          {
            role: 'agent',
            widget: (
              <BubbleAutopilot
                title={c.autopilotTitle}
                steps={[...c.autopilotSteps]}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.helpdesk['Get started in minutes']}
        subtitle={
          t.helpdesk[
            'No developers, no custom code, no months of building. Just AI that works.'
          ]
        }
        learnMoreLink="#comparison"
        accentColor="purple"
        theme="light"
        benefits={[
          {
            title: t.helpdesk['Create the AI agent in Twilio Flex'],
            description:
              t.helpdesk[
                'Our AI integrates directly into your Twilio Flex contact center as a production-ready agent.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl: '/channels/twilio-flex/create-the-ai-agent.png',
            imagePosition: 'right',
          },
          {
            title: t.helpdesk['Set the routing rules'],
            description:
              t.helpdesk[
                "Route 100% or 10% of your support — it's really up to you."
              ],
            linkText: '',
            linkHref: '#',
            imageUrl: '/channels/twilio-flex/set-the-routing-rules.png',
            imagePosition: 'left',
          },
          {
            title: t.helpdesk['Monitor and iterate'],
            description:
              t.helpdesk[
                'Seamlessly integrate with your existing technology stack, with comprehensive summaries and intelligent routing. Continuously improve with feedback.'
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '77%',
              label: t.helpdesk['Automation Rate'],
              sublabel: t.helpdesk['Industry-leading performance'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              <MagicalText>Agent 5</MagicalText> {c.vs}{' '}
              {t.helpdesk['Building Your Own']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  'See how Agent 5 saves you months of development and gives you production-ready AI instantly.'
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.helpdesk['Feature']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      <MagicalText>Agent 5</MagicalText> {t.helpdesk['(Open)']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.helpdesk['Custom Build on Flex']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/30 hover:bg-muted/30 transition-colors"
                    >
                      <TableCell className="text-foreground py-4 font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.agent5.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.agent5.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'agent5' &&
                            'bg-green-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('agent5')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.agent5.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.agent5.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.agent5.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.agent5.value}</span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.twilio.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.twilio.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'twilio' &&
                            'bg-red-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('twilio')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.twilio.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.twilio.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.twilio.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.twilio.value}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* Automate Twilio Flex Manual Work Section */}
      <section className="bg-neutral-50 py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.helpdesk['Automate the manual tasks, not just the answers']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  'In addition to fully resolving support tickets, Open automates the manual tasks that slow down your team—routing, tagging, QA, and more.'
                ]
              }
            </p>
          </div>
          <TemplatesList
            systemFilter="Twilio Flex"
            categoryFilter="Customer Support"
            limit={6}
            variant="slider"
          />
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.helpdesk[
            "Find answers to common questions about our Twilio Flex AI agent. Can't find what you're looking for? Contact our support team."
          ]
        }
        items={[
          {
            question: t.helpdesk['Does Twilio Flex have built-in AI agents?'],
            answer:
              t.helpdesk[
                "No. Twilio Flex is a powerful contact center platform but doesn't include native AI agents. You'd need to build and maintain your own AI solution, which requires significant development resources."
              ],
          },
          {
            question:
              t.helpdesk['How does Agent 5 integrate with Twilio Flex?'],
            answer: t.helpdesk['How does Agent 5 integrate with Twilio Flex?'],
          },
          {
            question:
              t.helpdesk[
                'Do I need developers to set up Agent 5 with Twilio Flex?'
              ],
            answer:
              t.helpdesk[
                'No. Unlike building your own AI solution for Flex which requires a development team, Agent 5 is production-ready and can be deployed in under an hour without any coding.'
              ],
          },
          {
            question:
              t.helpdesk[
                'What channels does Agent 5 support with Twilio Flex?'
              ],
            answer:
              t.helpdesk[
                'Agent 5 works across all Twilio Flex channels including voice, SMS, WhatsApp, chat, and email. It provides consistent AI support across your entire contact center.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Can I see Agent 5 performance in Twilio Flex dashboards?'
              ],
            answer:
              t.helpdesk[
                'Yes. Agent 5 appears as an agent in your Twilio Flex analytics, allowing you to track performance, monitor conversations, and measure AI effectiveness alongside your human agents.'
              ],
          },
          {
            question: t.helpdesk['How is Agent 5 maintained and updated?'],
            answer:
              t.helpdesk[
                'Agent 5 is fully managed by us. You get automatic updates, continuous improvements, and the latest AI capabilities without any maintenance work—unlike a custom-built solution that requires ongoing development.'
              ],
          },
        ]}
      />

      {/* Testimonials Section */}
      <Testimonials />
    </>
  );
}
